/**
 * Analytic references for the game's light model (see physics.ts) and for real lensing.
 */

/** Weak-field (small-angle) deflection of the game's force law: alpha = 2 mu / b, radians. */
export function weakDeflection(mu: number, b: number): number {
  return (2 * mu) / b;
}

/** Photon-sphere radius of the medium n = exp(mu / r): circular light orbits at r = mu. */
export function photonSphere(mu: number): number {
  return mu;
}

/** Rays with impact parameter below e * mu spiral into the centre (mu > 0). */
export function criticalImpact(mu: number): number {
  return Math.E * mu;
}

const phi = (mu: number, r: number) => r * Math.exp(mu / r);

/** Distance of closest approach r0 for impact parameter b (n r sin psi = b is conserved). */
export function closestApproach(mu: number, b: number): number {
  if (mu > 0 && b <= criticalImpact(mu)) return NaN;
  let lo = mu > 0 ? mu : 1e-12;
  let hi = b + 2 * Math.abs(mu) + 1;
  for (let i = 0; i < 200; i++) {
    const mid = 0.5 * (lo + hi);
    if (phi(mu, mid) < b) lo = mid;
    else hi = mid;
    if (hi - lo <= 1e-15 * hi) break;
  }
  // The upper end of the bracket satisfies n r >= b, so (n r)^2 - b^2 stays non-negative.
  return hi;
}

// 8-point Gauss-Legendre nodes and weights on [-1, 1].
const GL_X = [
  -0.9602898564975363, -0.7966664774136267, -0.525532409916329, -0.1834346424956498,
  0.1834346424956498, 0.525532409916329, 0.7966664774136267, 0.9602898564975363,
];
const GL_W = [
  0.1012285362903763, 0.2223810344533745, 0.3137066458778873, 0.362683783378362, 0.362683783378362,
  0.3137066458778873, 0.2223810344533745, 0.1012285362903763,
];

/**
 * Exact deflection angle (radians, positive = towards the mass) of a ray with impact parameter b
 * passing a single mass in the medium n = exp(mu / r):
 *
 *   chi = 2 * integral_{r0}^{inf} b dr / ( r sqrt( (n r)^2 - b^2 ) ) - pi.
 *
 * Substituting r = r0 / u and u = 1 - w^2 removes both endpoint singularities; the smooth
 * integrand is then integrated with composite Gauss-Legendre quadrature.
 * Returns NaN for captured rays (b <= e mu).
 */
export function exactDeflection(mu: number, b: number, panels = 400): number {
  const r0 = closestApproach(mu, b);
  if (!Number.isFinite(r0)) return NaN;
  // Use the invariant of the root actually found (equal to b within rounding).
  const L = phi(mu, r0);
  const e0 = Math.exp(mu / r0);
  const f = (w: number) => {
    const w2 = w * w;
    const u = 1 - w2;
    // r - r0 and n r - L without cancellation: with x = -mu d / (r0 (r0 + d)),
    // n r - L = e^(mu/r0) (r0 expm1(x) + d e^x).
    const d = (r0 * w2) / u;
    const x = (-mu * d) / (r0 * (r0 + d));
    const em1 = Math.expm1(x);
    const diff = e0 * (r0 * em1 + d * (1 + em1));
    const q = diff * (2 * L + diff);
    if (q <= 0) return 0;
    return (2 * w * L) / (u * Math.sqrt(q));
  };
  let sum = 0;
  for (let k = 0; k < panels; k++) {
    // Grade the panels towards w = 0 where the integrand varies fastest near the turning point.
    const a = (k / panels) ** 2;
    const c = ((k + 1) / panels) ** 2;
    const half = 0.5 * (c - a);
    const mid = 0.5 * (c + a);
    for (let j = 0; j < 8; j++) sum += GL_W[j]! * half * f(mid + half * GL_X[j]!);
  }
  return 2 * sum - Math.PI;
}

/**
 * Real light bending by a mass in general relativity (weak field): alpha = 4 G M / (c^2 b).
 * Inputs in SI units (GM in m^3 s^-2, b in metres); output in radians.
 */
export function einsteinDeflection(gm: number, b: number): number {
  const c = 299_792_458;
  return (4 * gm) / (c * c * b);
}

/** IAU 2015 nominal solar values (Resolution B3). */
export const SUN_GM = 1.3271244e20; // m^3 s^-2
export const SUN_RADIUS = 6.957e8; // m

export const ARCSEC_PER_RAD = (180 / Math.PI) * 3600;

/** Deflection of starlight grazing the Sun, arcseconds (about 1.75). */
export function solarGrazingArcsec(): number {
  return einsteinDeflection(SUN_GM, SUN_RADIUS) * ARCSEC_PER_RAD;
}
